import React from "react";
import { formatMinutes } from "@/utils/time";
import PT from "prop-types";
import * as S from "./styled";
import BagPlusIcon from "_c/icon/BagPlus";
import TrashIcon from "_c/icon/Trash";
import { CircleButton } from "_c/button";
import GearIcon from "_c/icon/Gear";
import CameraIcon from "_c/icon/Camera";
import ClockIcon from "_c/icon/Clock";
import ModelSearchIcon from "_c/icon/Lady";
import CameraAltIcon from "_c/icon/ModelSearch";
import Tooltip from "_c/Tooltip";
import { CurrencyFormatter } from "@/i18n/currency";
import { mul } from "@/utils/money";
import BasketUnitPopover from "_c/Popover/BasketUnitPicker";
import isUndefined from "lodash/isUndefined";
import DragHandle from "./DragHandle";

const ns = "master_profile:service_tips";
const ServiceView = ({
  isOwner,
  discount,
  isDiscountPersonal,
  isModelSearchDiscount,
  duration,
  title,
  oldPrice,
  price,
  desc,
  isInBasket,
  onBasketRemove,
  addToBasket,
  photoIdx,
  openGallery,
  toggleBonusEditor,
  openServiceEditor,
  bonusEditorOpen,
  serviceId,
  perUnit,
  uploadPhoto,
  unitName,
  canAddToBasket,
  isBookingDisabled,
  t,
  removeMut,
  isDraggable,
  dragHandleRef,
  dragHandleListeners,
}) => (
  <S.Wrap>
    <S.LeftPart>
      {isDraggable && (
        <DragHandle
          dragHandleRef={dragHandleRef}
          dragHandleListeners={dragHandleListeners}
        />
      )}
      <div>
        <S.TitleAndDiscountWrap>
          <S.Title>{title}</S.Title>
          {discount && (
            <S.DiscountsWrap>
              <Tooltip
                title={
                  isDiscountPersonal
                    ? t(`${ns}.personal_discount`)
                    : t(`${ns}.normal_discount`)
                }
              >
                <S.ActiveDiscount>{`-${mul(
                  discount.amount,
                  100
                )}%`}</S.ActiveDiscount>
              </Tooltip>
              {isModelSearchDiscount && (
                <Tooltip title={t("common:model_search")}>
                  <S.ActiveDiscount model>
                    <ModelSearchIcon width="18" color="white" />
                  </S.ActiveDiscount>
                </Tooltip>
              )}
            </S.DiscountsWrap>
          )}
        </S.TitleAndDiscountWrap>
        <S.Info>
          {oldPrice && (
            <S.OldPrice as="strike">
              <span>{CurrencyFormatter.format(oldPrice)}</span>
            </S.OldPrice>
          )}
          <S.Price>
            <span>{CurrencyFormatter.format(price)}</span>
            {perUnit && (
              <>
                <S.PriceUnitDelim>/</S.PriceUnitDelim>
                <S.Unit>{unitName}</S.Unit>
              </>
            )}
          </S.Price>
          <S.Duration>
            <ClockIcon width="14" color="var(--font-color-emp)" />
            <span>{formatMinutes(duration)}</span>
          </S.Duration>
          {desc && (
            <Tooltip title={desc}>
              <S.DescLabel small>i</S.DescLabel>
            </Tooltip>
          )}
        </S.Info>
        {isBookingDisabled && (
          <div
            css={`
              color: var(--color-danger);
              @media (max-width: 550px) {
                margin-bottom: 10px;
              }
            `}
          >
            {t("master_profile:service_booking_disabled")}
          </div>
        )}
      </div>
    </S.LeftPart>
    <S.RightPart>
      {!isUndefined(photoIdx) && (
        <CircleButton
          style={{ margin: "0 8px" }}
          onClick={openGallery}
          medium
          title={t("common:open")}
        >
          <CameraAltIcon width="15" color="white" />
        </CircleButton>
      )}
      {Boolean(isInBasket) && (
        <CircleButton
          negative
          onClick={onBasketRemove}
          title={t(`${ns}.remove_basket`)}
        >
          <TrashIcon width="20px" color="white" />
        </CircleButton>
      )}
      {!isBookingDisabled && !isOwner && !isInBasket && canAddToBasket && (
        <>
          {perUnit ? (
            <BasketUnitPopover t={t} onSubmit={addToBasket} unitName={unitName}>
              <S.ShopButton title={t(`${ns}.add_basket`)}>
                <BagPlusIcon color="white" width="17" />
              </S.ShopButton>
            </BasketUnitPopover>
          ) : (
            <S.ShopButton onClick={addToBasket} title={t(`${ns}.add_basket`)}>
              <BagPlusIcon color="white" width="17" />
            </S.ShopButton>
          )}
        </>
      )}
      {isOwner && (
        <>
          <CircleButton
            onClick={uploadPhoto}
            title={t("common:add_photo")}
            medium
          >
            <CameraIcon color="white" width="18" />
          </CircleButton>
          <S.DiscountInput
            aria-expanded={bonusEditorOpen}
            aria-controls={`bonus-editor__${serviceId}-container`}
            title={t(`${ns}.set_discount`)}
            onClick={toggleBonusEditor}
          >
            %
          </S.DiscountInput>
          <CircleButton
            primary
            onClick={openServiceEditor}
            title={t(`${ns}.change`)}
          >
            <GearIcon width="20" color="white" />
          </CircleButton>
          <CircleButton
            css={`
              margin-inline-start: 8px;
            `}
            negative
            onClick={removeMut.mutate}
            disabled={removeMut.isLoading}
            title={t(`${ns}.remove_service`)}
          >
            <TrashIcon width="20" color="white" />
          </CircleButton>
        </>
      )}
    </S.RightPart>
  </S.Wrap>
);
ServiceView.propTypes = {
  discount: PT.object,
  isDiscountPersonal: PT.bool,
  isModelSearchDiscount: PT.bool,
  duration: PT.number,
  price: PT.object,
  desc: PT.string,
  isInBasket: PT.bool,
  isOwner: PT.bool,
  basketAmount: PT.number,
  title: PT.string,
  editorOpen: PT.bool,
  serviceId: PT.string,
  perUnit: PT.bool,
  unitName: PT.string,
  canAddToBasket: PT.bool,
  theme: PT.object,
  isBookingDisabled: PT.bool,
  removeMut: PT.object.isRequired,

  openServiceEditor: PT.func.isRequired,
  uploadPhoto: PT.func.isRequired,
  t: PT.func.isRequired,
  toggleBonusEditor: PT.func.isRequired,
  addToBasket: PT.func,
  onBasketRemove: PT.func,
};

export default ServiceView;
